import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormControl,
  Row,
  Dropdown,
} from "react-bootstrap";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link } from "react-router-dom";
import "./style.css";

// ---------------------------
// TYPES
// ---------------------------
interface DeliveryBoy {
  firstName: string;
  phoneNumber: string;
}

interface LocationInfo {
  address: string;
}

interface OrderView {
  orderId: string;
  orderStatus: string;
  customer_name: string;
  customer_phone_number: string;
  delivery_boy_details: DeliveryBoy;
  delivery_location: LocationInfo;
  recollection_location: LocationInfo;
  createdAt: string;
}

interface OrderServiceDetailsModel {
  erpOrderNumber: string;
  serviceName: string;
}

interface Order {
  orderView: OrderView;
  orderServiceDetailsModel: OrderServiceDetailsModel;
}

// ---------------------------
// HELPERS
// ---------------------------
const formatDate = (value: string): string => {
  if (!value) return "N/A";
  const d = new Date(value);
  return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getFullYear()} ${d
    .getHours()
    .toString()
    .padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
};

// UI LABEL MAPPER
const mapStatusLabel = (status: string): string => {
  switch (status) {
    case "ORDER_ACCEPT":
      return "ACTIVE ORDERS";
    case "CANCELLED":
      return "CANCELLED";
    case "COMPLETED":
      return "COMPLETED";
    default:
      return status;
  }
};

// ---------------------------
// MAIN COMPONENT
// ---------------------------
const VendorServiceList = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [searchCriteria, setSearchCriteria] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // TAB STATUS
  const [selectedTab, setSelectedTab] = useState<string>("ORDER_ACCEPT"); // Default NEW ORDERS

  // ---------------------------
  // API CALL — GET_ORDERS
  // ---------------------------
  const fetchOrders = useCallback(async () => {
    const payload = {
      status: selectedTab || "", // <-- tab status inserted here
      phone_number: searchCriteria === "phone_number" ? searchQuery : "",
      erp_service_number:
        searchCriteria === "erp_service_number" ? searchQuery : "",
      order_number: searchCriteria === "order_number" ? searchQuery : "",
      delivery_partner_status:
        searchCriteria === "delivery_partner_status" ? searchQuery : "",
    };

    try {
      const res = await axiosInstance.post(
        `${API_URL.GET_ORDERS}?page=${page}&size=${size}`,
        payload
      );

      setOrders(res.data?.content || []);
      setTotalPages(res.data?.totalPages || 0);
    } catch (error) {
      console.error("GET_ORDERS failed:", error);
    }
  }, [page, size, selectedTab, searchCriteria, searchQuery]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ---------------------------
  // STATUS TABS ARRAY
  // ---------------------------
  const tabs = [
    { key: "ORDER_ACCEPT", label: "ACTIVE ORDERS" },
    { key: "CANCELLED", label: "CANCELLED" },
    { key: "COMPLETED", label: "COMPLETED" },
  ];

  return (
    <div className="row vendor-service-list-page">
      <Card className="card-bg-afl text-start">
        <CardBody className="px-0 pt-1">
          {/* --------------------- STATUS TABS --------------------- */}
          <div className="d-flex gap-3 mb-3">
            {tabs.map((tab) => {
              const isActive = selectedTab === tab.key;

              return (
                <Button
                  key={tab.key}
                  onClick={() => {
                    setSelectedTab(tab.key);
                    setPage(0);
                  }}
                  variant={isActive ? "primary" : "outline-secondary"}
                  className={`status-tab-btn ${isActive ? "active-tab" : ""}`}
                >
                  {tab.label}
                </Button>
              );
            })}
          </div>

          {/* --------------------- SEARCH UI --------------------- */}
          <Row className="align-items-center mb-3">
            <Col md="3">
              <Form.Select
                className="select-invoice-bg-black-edit dashboard-poppins-font-style "
                value={searchCriteria}
                onChange={(e) => {
                  setSearchCriteria(e.target.value);
                  setSearchQuery("");
                }}
              >
                <option value="">Search by</option>
                <option value="phone_number">Phone Number</option>
                <option value="erp_service_number">ERP Service Number</option>
                <option value="order_number">Order Number</option>
                <option value="delivery_partner_status">
                  Delivery Partner Status
                </option>
              </Form.Select>
            </Col>

            {searchCriteria && (
              <Col md="4">
                <FormControl
                  placeholder="Enter search value..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Col>
            )}

            <Col md="2">
              <Button
                onClick={() => {
                  setPage(0);
                  fetchOrders();
                }}
                className="btn all_button_style text-black my-1"
              >
                Search
              </Button>
            </Col>
          </Row>

          {/* --------------------- TABLE --------------------- */}
          <div className="col-12 dashboard-table-div-vendor">
            <table className="table table-hover dashboard-table-vendor table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>#</th>
                  <th>Order No</th>
                  <th>ERP Service No</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Service</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Created</th>
                </tr>
              </thead>

              <tbody>
                {orders.map((item, index) => {
                  const view = item.orderView;
                  const details = item.orderServiceDetailsModel;

                  return (
                    <tr key={index}>
                      <td className="table-action-btn">
                        <Dropdown onClick={(e) => e.stopPropagation()}>
                          <Dropdown.Toggle
                            variant="success"
                            id="dropdown-basic"
                            className="Dropdown-table-list Dropdown-table-border-bottom border-none"
                          >
                            <MoreVertIcon
                              style={{
                                color: "#333134",
                                fontSize: "20px",
                              }}
                            />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="Dropdown-table-item border-none">
                            <Dropdown.Item
                              as={Link}
                              to={`/vendor/dashboard/servicelist/servicedetails?orderId=${view?.orderId}&page=${page}&size=${size}`}
                              className="Dropdown-table-list Dropdown-table-border-bottom border-none"
                            >
                              View Details
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                      <td>{index + 1 + page * size}</td>
                      <td>{view?.orderId}</td>
                      <td>{details?.erpOrderNumber || "N/A"}</td>
                      <td>{view?.customer_name || "N/A"}</td>
                      <td>{view?.customer_phone_number || "N/A"}</td>

                      {/* STATUS */}
                      <td>
                        <span className="badge bg-primary">
                          {mapStatusLabel(view?.orderStatus)}
                        </span>
                      </td>

                      <td>{details?.serviceName || "N/A"}</td>

                      {/* FROM / TO LOCATION */}
                      <td>{view?.delivery_location?.address || "-"}</td>
                      <td>{view?.recollection_location?.address || "-"}</td>

                      <td>{formatDate(view?.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* --------------------- PAGINATION --------------------- */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button
              className="btn all_button_style text-black my-1"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </Button>

            <span>
              Page {page + 1} / {totalPages}
            </span>

            <Button
              className="btn all_button_style text-black my-1"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default VendorServiceList;
